import './AppModal.scss';
import cn from 'classnames';
import { ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Button, ButtonDesignType } from '../Button';
import { Appearance, AppearanceType, Design, Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';

interface AppModalAction {
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  appearance?: AppearanceType;
  design?: ButtonDesignType;
}

type AppModalTertiaryAction = AppModalAction & { onClick: () => void; label: string };

interface AppModalProps {
  show: boolean;
  onHide: () => void;
  title: ReactNode;
  size?: SizeType;
  /** Replaces the action buttons. */
  footer?: ReactNode;
  /** Rendered when set. Does not close the modal; call `onHide` yourself. */
  primaryAction?: AppModalAction;
  /** Rendered only when `onClick` is set. Defaults to "Cancel", outlined neutral. */
  secondaryAction?: AppModalAction;
  /** Rendered on the left of the footer. */
  tertiaryAction?: AppModalTertiaryAction;
  /** Caps the modal at the viewport height and scrolls the body when content overflows. */
  scrollBody?: boolean;
  /** Puts the primary button in its loading state and disables the other buttons. */
  isLoading?: boolean;
  /** When false, or while loading, the backdrop and Escape do not close the modal. */
  escapable?: boolean;
  dataTestId?: string;
  children?: ReactNode;
}

export const AppModal = ({
  show,
  onHide,
  title,
  size = Size.MD,
  footer,
  primaryAction,
  secondaryAction = {},
  tertiaryAction,
  scrollBody = true,
  isLoading,
  escapable = true,
  dataTestId,
  children
}: AppModalProps): ReactNode => {
  const hasActions = Boolean(primaryAction || secondaryAction.onClick || tertiaryAction);
  const canDismiss = escapable && !isLoading;

  return (
    <Modal
      data-testid={dataTestId}
      show={show}
      onHide={onHide}
      centered
      scrollable={scrollBody}
      dialogClassName={cn('app-modal', `app-modal-${size}`)}
      backdropClassName='app-modal-backdrop'
      contentClassName='app-modal-content'
      backdrop={canDismiss ? true : 'static'}
      keyboard={canDismiss}
    >
      <Modal.Header>
        <Modal.Title as='h4'>{title}</Modal.Title>
        <IconButton
          aria-label='Close'
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          disabled={isLoading}
          onClick={onHide}
        >
          <MdClose />
        </IconButton>
      </Modal.Header>
      <Modal.Body>
        <div className='app-modal-body-content'>{children}</div>
      </Modal.Body>
      {(footer || hasActions) && (
        <Modal.Footer>
          {footer || (
            <>
              {tertiaryAction && (
                <Button
                  className='app-modal-tertiary'
                  disabled={isLoading || tertiaryAction.disabled}
                  appearance={tertiaryAction.appearance ?? Appearance.EMPTY}
                  design={tertiaryAction.design}
                  onClick={tertiaryAction.onClick}
                >
                  {tertiaryAction.label}
                </Button>
              )}
              {secondaryAction.onClick && (
                <Button
                  disabled={isLoading || secondaryAction.disabled}
                  appearance={secondaryAction.appearance ?? Appearance.OUTLINED}
                  design={secondaryAction.design ?? Design.NEUTRAL}
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.label ?? 'Cancel'}
                </Button>
              )}
              {primaryAction && (
                <Button
                  loading={isLoading}
                  disabled={primaryAction.disabled}
                  appearance={primaryAction.appearance}
                  design={primaryAction.design}
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.label ?? 'Okay'}
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      )}
    </Modal>
  );
};
